import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'asana-trigger',
  version: '1.0.0',
  properties: [
    // === Authentication ===
    {
      key: 'credentialId',
      label: 'Asana Credential',
      type: 'credential',
      required: true,
      description: 'Select the Asana OAuth2 credential to use',
      typeOptions: {
        credentialType: 'asana_oauth2',
      },
    },

    // === Workspace selector (modeToggle) ===
    {
      key: 'workspaceGid',
      label: 'Workspace',
      type: 'string',
      required: true,
      description: 'The workspace to watch for events in',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getWorkspaces',
          nodeType: 'asana',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
      },
    },

    // === Resource type to watch ===
    {
      key: 'resourceType',
      label: 'Watch',
      type: 'options',
      default: 'project',
      required: true,
      description: 'The type of Asana resource to watch for events',
      options: [
        { name: 'Project', value: 'project', description: 'Watch events on a project (tasks added, changed, etc.)' },
        { name: 'Task', value: 'task', description: 'Watch events on a specific task (comments, changes, etc.)' },
      ],
    },

    // === Project selector (modeToggle, shown when watching a project) ===
    {
      key: 'resourceGid',
      label: 'Project',
      type: 'string',
      required: true,
      description: 'The project to watch for events',
      displayOptions: {
        show: { resourceType: ['project'] },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getProjects',
          nodeType: 'asana',
          dependsOn: ['workspaceGid'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
      },
    },

    // === Task GID (text input, shown when watching a task) ===
    {
      key: 'resourceGid',
      label: 'Task GID',
      type: 'string',
      required: true,
      description: 'The GID of the Asana task to watch for events. You can find this in the task URL.',
      placeholder: 'e.g. 1234567890123456',
      displayOptions: {
        show: { resourceType: ['task'] },
      },
    },

    // === Polling Configuration ===
    {
      key: 'pollMode',
      label: 'Poll Frequency',
      type: 'options',
      default: 'everyXMinutes',
      description: 'How often to check for new events',
      options: [
        { name: 'Every X Minutes', value: 'everyXMinutes' },
        { name: 'Every Hour', value: 'everyHour' },
        { name: 'Every Day', value: 'everyDay' },
        { name: 'Custom (Cron)', value: 'custom' },
      ],
    },
    {
      key: 'minuteInterval',
      label: 'Poll Every',
      type: 'options',
      default: 5,
      description: 'Poll for events every X minutes',
      options: [
        { name: '1 minute', value: 1 },
        { name: '5 minutes', value: 5 },
        { name: '10 minutes', value: 10 },
        { name: '15 minutes', value: 15 },
        { name: '30 minutes', value: 30 },
      ],
      displayOptions: {
        show: { pollMode: ['everyXMinutes'] },
      },
    },
    {
      key: 'minute',
      label: 'Minute',
      type: 'number',
      default: 0,
      description: 'Minute of the hour (0-59)',
      displayOptions: {
        show: { pollMode: ['everyHour', 'everyDay'] },
      },
      typeOptions: { minValue: 0, maxValue: 59 },
    },
    {
      key: 'hour',
      label: 'Hour',
      type: 'number',
      default: 9,
      description: 'Hour of the day (0-23)',
      displayOptions: {
        show: { pollMode: ['everyDay'] },
      },
      typeOptions: { minValue: 0, maxValue: 23 },
    },
    {
      key: 'cronExpression',
      label: 'Cron Expression',
      type: 'string',
      default: '*/5 * * * *',
      placeholder: '*/5 * * * *',
      description: 'Custom cron expression',
      displayOptions: {
        show: { pollMode: ['custom'] },
      },
    },
    {
      key: 'timezone',
      label: 'Timezone',
      type: 'options',
      default: 'UTC',
      description: 'Timezone for the polling schedule',
      options: [
        { value: 'UTC', name: 'UTC' },
        { value: 'America/New_York', name: 'Eastern Time (US)' },
        { value: 'America/Chicago', name: 'Central Time (US)' },
        { value: 'America/Los_Angeles', name: 'Pacific Time (US)' },
        { value: 'Europe/London', name: 'London' },
        { value: 'Europe/Paris', name: 'Paris' },
        { value: 'Europe/Berlin', name: 'Berlin' },
        { value: 'Asia/Tokyo', name: 'Tokyo' },
        { value: 'Australia/Sydney', name: 'Sydney' },
      ],
    },
  ],
};

export default schema;
